import { ListFilterIcon, PlusIcon } from 'lucide-react';

import type { SessionChargeId } from '@nesso/ledger-contracts/ids';
import { EmptyTableState } from '@nesso/ui/blocks/table/empty-table-state';
import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import { Button } from '@nesso/ui/primitives/button';
import { Item, ItemActions, ItemContent, ItemTitle } from '@nesso/ui/primitives/item';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@nesso/ui/primitives/select';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@nesso/ui/primitives/table';

import type { GetPaymentListOutput, Payment } from '@/entities/payment';
import { useLoadMoreOnVisible } from '@/shared/lib/use-load-more-on-visible';
import { CenteredSpinner } from '@/shared/ui/indicators/centered-spinner';
import { DataSpinner } from '@/shared/ui/indicators/data-spinner';

import {
    type ActivityFilterValue,
    CLIENT_BILLING_ACTIVITY_FILTER_OPTIONS,
} from '../../config/client-billing-activity-filter';
import { ClientBillingSheetActivityPayment } from './client-billing-sheet-activity/client-billing-sheet-activity-payment';
import { ClientBillingSheetActivityRefund } from './client-billing-sheet-activity/client-billing-sheet-activity-refund';

type Props = {
    pages: GetPaymentListOutput[] | undefined;
    isError: boolean;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    isFetchNextPageError: boolean;
    fetchNextPage: () => void;
    unpaidCount: number;
    filter: ActivityFilterValue;
    onFilterChange: (filter: ActivityFilterValue) => void;
    getServiceLabel: (serviceCode: string) => string;
    onAddPayment: (sessionChargeId: SessionChargeId | null) => void;
    onOpenAppliedTo: (paymentId: string, sessionChargeId: string) => void;
    onOpenOriginalPayment: (payment: Payment) => void;
    onRefund: (payment: Payment) => void;
};

export function ClientBillingSheetActivity({
    pages,
    isError,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    unpaidCount,
    filter,
    onFilterChange,
    getServiceLabel,
    onAddPayment,
    onOpenAppliedTo,
    onOpenOriginalPayment,
    onRefund,
}: Props) {
    const isCompact = useBreakpointDown('md');
    const sentinelRef = useLoadMoreOnVisible({
        hasNextPage: hasNextPage && !isFetchNextPageError,
        isFetchingNextPage,
        fetchNextPage,
    });

    const columnCount = isCompact ? 4 : 6;
    const isLoading = !pages && !isError;

    function renderBody() {
        if (!pages) {
            return (
                <EmptyTableState
                    colSpan={columnCount}
                    message="Couldn't load activity"
                    tone="default"
                />
            );
        }

        const items = pages.flatMap((page) => page.items);

        if (items.length === 0) {
            return (
                <EmptyTableState colSpan={columnCount} message="No activity yet" tone="default" />
            );
        }

        return items.map((item) =>
            item.kind === 'REFUND' ? (
                <ClientBillingSheetActivityRefund
                    key={item.id}
                    refund={item}
                    isCompact={isCompact}
                    onOpenOriginalPayment={onOpenOriginalPayment}
                />
            ) : (
                <ClientBillingSheetActivityPayment
                    key={item.id}
                    payment={item}
                    isCompact={isCompact}
                    getServiceLabel={getServiceLabel}
                    onOpenAppliedTo={onOpenAppliedTo}
                    onRefund={onRefund}
                />
            ),
        );
    }

    return (
        <section className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-lg font-bold">Activity</h3>
                <div className="flex items-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={unpaidCount === 0}
                        onClick={() => onAddPayment(null)}
                    >
                        <PlusIcon />
                        Payment
                    </Button>
                    <Select
                        value={filter}
                        onValueChange={(value) => {
                            const option = CLIENT_BILLING_ACTIVITY_FILTER_OPTIONS.find(
                                (candidate) => candidate.value === value,
                            );
                            if (option) onFilterChange(option.value);
                        }}
                    >
                        <SelectTrigger
                            aria-label="Filter activity"
                            variant="ghost"
                            className="w-auto"
                        >
                            <div className="flex items-center gap-2">
                                <ListFilterIcon className="size-4 text-foreground" />
                                <SelectValue />
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            {CLIENT_BILLING_ACTIVITY_FILTER_OPTIONS.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            {isLoading ? (
                <CenteredSpinner label="Loading activity" className="py-12" />
            ) : (
                <>
                    <Table className="table-fixed">
                        <TableHeader>
                            <TableRow>
                                <TableHead variant="plain" className="w-26 md:w-24">
                                    Date
                                </TableHead>
                                <TableHead variant="plain" className="w-26 md:w-38">
                                    ID
                                </TableHead>
                                {!isCompact && (
                                    <>
                                        <TableHead variant="plain">Method</TableHead>
                                        <TableHead variant="plain">Applied to</TableHead>
                                    </>
                                )}
                                <TableHead variant="plain" className="text-right">
                                    Amount
                                </TableHead>
                                <TableHead variant="plain" className="w-11 md:w-15.5">
                                    <span className="sr-only">Details</span>
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>{renderBody()}</TableBody>
                    </Table>
                    <div ref={sentinelRef} className="flex justify-center">
                        {isFetchingNextPage && (
                            <div role="status" aria-label="Loading more activity">
                                <DataSpinner />
                            </div>
                        )}
                    </div>
                    {isFetchNextPageError && (
                        <Item variant="muted" alignment="center">
                            <ItemContent>
                                <ItemTitle className="leading-5">
                                    Couldn't load more activity
                                </ItemTitle>
                            </ItemContent>
                            <ItemActions>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={fetchNextPage}
                                >
                                    Retry
                                </Button>
                            </ItemActions>
                        </Item>
                    )}
                </>
            )}
        </section>
    );
}
